import React, { useCallback, useEffect, useState } from 'react';
import {
  PageHeader, Panel, Button, IconButton, Tag, Banner, EmptyState, Spinner, Dialog, ConfirmDialog, Field, TextInput, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import ImportSectionNext from '../../components/ImportSectionNext.jsx';
import { formatHebrewDate } from '../../utils/hebrewDate';
import { apiError } from '../../next/ui';

/**
 * זמני מבחנים — new design. Same endpoints as ExamPeriodsList: create / rename / activate / end registration / delete,
 * last issuance per period, backfill registrations and grades (dry-run count first), grade imports per period.
 */
export default function ExamPeriodsListNext() {
  const toast = useCkToast();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [backfillTarget, setBackfillTarget] = useState(null);
  const [backfillCount, setBackfillCount] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [gradesTarget, setGradesTarget] = useState(null);
  const [gradesStats, setGradesStats] = useState(null);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [issuanceMap, setIssuanceMap] = useState({});
  const [openImports, setOpenImports] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/exam-periods');
      const list = data.data || [];
      setPeriods(list);
      const iMap = {};
      await Promise.all(list.map(async (p) => {
        try {
          const { data: last } = await api.get(`/exam-issuance/last?examPeriodId=${p._id}`);
          if (last) iMap[p._id] = last;
        } catch { /* ignore */ }
      }));
      setIssuanceMap(iMap);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת זמני מבחנים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => { setEditId(null); setName(''); setDialogOpen(true); };
  const openEdit = (p) => { setEditId(p._id); setName(p.name); setDialogOpen(true); };
  const save = async () => {
    try {
      if (editId) { await api.put(`/exam-periods/${editId}`, { name }); toast.success('הזמן עודכן'); }
      else { await api.post('/exam-periods', { name }); toast.success('הזמן נוצר'); }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };
  const activate = async (id) => {
    try {
      await api.post(`/exam-periods/${id}/activate`);
      toast.success('הזמן הופעל');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };
  const deactivateAll = async () => {
    try {
      await api.post('/exam-periods/deactivate-all');
      toast.success('הרישום הסתיים — אין מועד פעיל');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setDeactivateConfirm(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/exam-periods/${deleteTarget._id}`);
      toast.success('הזמן נמחק');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const openBackfill = async (p) => {
    setBackfillTarget(p);
    setBackfillCount(null);
    try {
      const { data } = await api.post(`/exam-periods/${p._id}/backfill?dryRun=1`);
      setBackfillCount(data.count);
    } catch {
      toast.error('שגיאה בספירת רשומות');
      setBackfillTarget(null);
    }
  };
  const runBackfill = async () => {
    setBackfillLoading(true);
    try {
      const { data } = await api.post(`/exam-periods/${backfillTarget._id}/backfill`);
      toast.success(`שויכו ${data.updated} נרשמים למועד "${data.periodName}"`);
      setBackfillTarget(null);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setBackfillLoading(false);
    }
  };
  const openGrades = async (p) => {
    setGradesTarget(p);
    setGradesStats(null);
    try {
      const { data } = await api.post(`/exam-periods/${p._id}/backfill-grades?dryRun=1`);
      setGradesStats({ byRegistration: data.byRegistration ?? data.count ?? 0, orphanTotal: data.orphanTotal ?? 0 });
    } catch {
      toast.error('שגיאה בספירת ציונים');
      setGradesTarget(null);
    }
  };
  const runGrades = async (assignAll) => {
    setGradesLoading(true);
    try {
      const url = assignAll ? `/exam-periods/${gradesTarget._id}/backfill-grades?assignAll=1` : `/exam-periods/${gradesTarget._id}/backfill-grades`;
      const { data } = await api.post(url);
      toast.success(`שויכו ${data.updated} ציונים למועד "${data.periodName}"`);
      setGradesTarget(null);
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setGradesLoading(false);
    }
  };

  const active = periods.find((p) => p.isActive);

  return (
    <div className="nx-stack">
      <PageHeader
        title="זמני מבחנים"
        sub={active ? `פעיל: ${active.name} · ${periods.length} זמנים` : `${periods.length} זמנים · אין מועד פעיל`}
        primary={{ label: 'זמן חדש', onClick: openCreate }}
      >
        {active ? <Button icon="pause" onClick={() => setDeactivateConfirm(true)}>סיים רישום</Button> : null}
      </PageHeader>
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={loadData}>נסו שוב</Button>}>{error}</Banner> : null}

      {loading ? <div className="ck-empty"><Spinner size={26} /></div> : periods.length === 0 ? (
        <EmptyState icon="calendar" title="אין זמני מבחנים" action={<Button variant="primary" icon="plus" onClick={openCreate}>זמן חדש</Button>}>צרו זמן ראשון כדי להתחיל.</EmptyState>
      ) : periods.map((p) => {
        const issued = issuanceMap[p._id];
        return (
          <Panel
            key={p._id}
            title={p.name}
            sub={`מועד מספר: ${p.order + 1}`}
            icon="calendar"
            tone={p.isActive ? 'primary' : 'neutral'}
            className={p.isActive ? 'nx-panel-on' : ''}
            actions={(
              <>
                {p.isActive ? <Tag tone="primary" icon="check">פעיל</Tag> : <Button size="sm" variant="primary" icon="check" onClick={() => activate(p._id)}>הפעל</Button>}
                <IconButton icon="edit" label="שינוי שם" onClick={() => openEdit(p)} />
                <IconButton icon="trash" label={p.isActive ? 'אי אפשר למחוק מועד פעיל' : 'מחיקה'} disabled={p.isActive} onClick={() => setDeleteTarget(p)} />
              </>
            )}
          >
            <div className="nx-stack">
              <div className="nx-item__tags">
                {issued ? (
                  <Tag tone={issued.isPartial ? 'warning' : 'success'} icon="file">{`הונפק ${formatHebrewDate(issued.issuedAt)}${issued.isPartial ? ' (חלקי)' : ''}`}</Tag>
                ) : <Tag>טרם הונפקו בחינות</Tag>}
              </div>
              <div className="nx-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Button size="sm" icon="inbox" onClick={() => openBackfill(p)}>שייך נרשמים ללא מועד</Button>
                <Button size="sm" icon="inbox" onClick={() => openGrades(p)}>שייך ציונים ללא מועד</Button>
                <Button size="sm" variant="ghost" icon={openImports === p._id ? 'up' : 'upload'} onClick={() => setOpenImports((cur) => (cur === p._id ? null : p._id))}>
                  {openImports === p._id ? 'הסתרת ייבוא ציונים' : 'ייבוא ציונים למועד'}
                </Button>
              </div>
              {openImports === p._id ? (
                <div className="nx-stack" style={{ gap: 8 }}>
                  <ImportSectionNext label={`ציונים — ${p.name}`} endpoint="/import/grades" templateEndpoint="/import/template/grades" description="עמודות: קוד אישי, קוד מבחן, ציון — המועד נשמר על הציון" extraFormData={{ periodId: p._id }} periodName={p.name} />
                  <ImportSectionNext label={`ציוני גמרא — ${p.name}`} endpoint="/import/gemara-grades" templateEndpoint="/import/template/gemara-grades" description="עמודות: קוד אישי + עמודות חוזרות של מסכת / דפים / ציון" extraFormData={{ periodId: p._id }} periodName={p.name} />
                </div>
              ) : null}
            </div>
          </Panel>
        );
      })}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? 'עריכת זמן' : 'זמן חדש'}
        icon="calendar"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={!name.trim()} onClick={save}>{editId ? 'שמירה' : 'יצירה'}</Button>
          </>
        )}
      >
        <Field label="שם (למשל: ניסן פ״ו)"><TextInput value={name} onChange={(e) => setName(e.target.value)} data-autofocus onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) save(); }} /></Field>
      </Dialog>

      <ConfirmDialog
        open={deactivateConfirm}
        onClose={() => setDeactivateConfirm(false)}
        onConfirm={deactivateAll}
        title="סיום רישום"
        message={<p className="ck-confirm__msg">לסיים את תקופת הרישום? המועד הפעיל יכבה ולא יהיה ניתן להירשם דרך המערכת. הנרשמים הקיימים עדיין ישמרו.</p>}
        confirmLabel="סיים רישום"
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        title="מחיקת זמן"
        message={<p className="ck-confirm__msg">{`למחוק את הזמן «${deleteTarget?.name || ''}»?`}</p>}
        confirmLabel="מחיקה"
      />

      <Dialog
        open={!!backfillTarget}
        onClose={() => !backfillLoading && setBackfillTarget(null)}
        title="שיוך נרשמים ללא מועד"
        icon="inbox"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={backfillLoading} onClick={() => setBackfillTarget(null)}>ביטול</Button>
            <Button variant="primary" disabled={backfillLoading || backfillCount === null || backfillCount === 0} onClick={runBackfill}>{backfillLoading ? 'משייך…' : 'שייך'}</Button>
          </>
        )}
      >
        {backfillCount === null ? <div className="nx-row" style={{ gap: 8 }}><Spinner size={18} /><span>סופר רשומות…</span></div> : (
          <p className="nx-desc">נמצאו <b>{backfillCount}</b> נרשמים ללא שיוך למועד. לשייך אותם למועד <b>{backfillTarget?.name}</b>?</p>
        )}
      </Dialog>

      <Dialog
        open={!!gradesTarget}
        onClose={() => !gradesLoading && setGradesTarget(null)}
        title={`שיוך מועד לציונים — ${gradesTarget?.name || ''}`}
        icon="inbox"
        size="md"
        footer={(
          <>
            <Button variant="ghost" disabled={gradesLoading} onClick={() => setGradesTarget(null)}>ביטול</Button>
            <Button disabled={gradesLoading || !gradesStats || gradesStats.byRegistration === 0} onClick={() => runGrades(false)}>לפי הרשמות</Button>
            <Button variant="primary" disabled={gradesLoading || !gradesStats || gradesStats.orphanTotal === 0} onClick={() => runGrades(true)}>{gradesLoading ? 'משייך…' : 'כל הציונים ללא מועד'}</Button>
          </>
        )}
      >
        {gradesStats === null ? <div className="nx-row" style={{ gap: 8 }}><Spinner size={18} /><span>סופר ציונים…</span></div> : (
          <div className="nx-stack">
            <p className="nx-desc"><b>{gradesStats.byRegistration}</b> ציונים ללא מועד שיש להם הרשמה במועד זה.</p>
            <p className="nx-desc">סה״כ <b>{gradesStats.orphanTotal}</b> ציוני הלכה במערכת ללא מועד.</p>
            {gradesStats.orphanTotal === 0 ? <Banner tone="info" icon="info">כל הציונים כבר משויכים למועד.</Banner> : null}
          </div>
        )}
      </Dialog>
    </div>
  );
}
